import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BsSearch } from "react-icons/bs";
import { get as commsGet } from "../../../service/comms/commsApi";
import { listRooms, ROOMS_PAGE_LIMIT } from "../../../service/comms/roomsService";
import { useUserChannel } from "../../../hooks/comms/useUserChannel";
import { useCommsIdentity } from "../../../hooks/comms/useCommsIdentity";
import { useRoomsUnread } from "../../../hooks/comms/RoomsUnreadProvider";
import { StatusBadge } from "../../../common/comms/StatusBadge";
import Loader from "../../../common/Loader/Loader";
import { relativeTime } from "../../../utils/oversightFormat";
import { useClientInsights } from "../../../hooks/insights/useClientInsights";
import { SegmentedControl } from "../../../common/floatingDock/SegmentedControl";
import { FiltersButton } from "../../../common/comms/rosterTags/FiltersButton";
import { FiltersPopover } from "../../../common/comms/rosterTags/FiltersPopover";
import { RosterSignalChip } from "../../../common/comms/rosterTags/RosterSignalChip";
import { FILTER_IDS } from "../../../common/comms/rosterTags/rosterTagDefs";
import {
  toggleFilter,
  matchesFilters,
  countMatches,
  availableFilterIds,
  hasInsightFilter,
  compareByStalenessDesc,
  describeEmptyState,
  SEGMENTS,
  currentSegment,
  setSegment,
  countActiveInsightFilters,
} from "../../../common/comms/rosterTags/applyRosterTags";

const SEARCH_DEBOUNCE_MS = 300;
const REFRESH_DEBOUNCE_MS = 400;
const POLL_INTERVAL_MS = 30000;

/**
 * Filters offered inside the Filters popover. Unread and Tagged are absent — the
 * segmented control owns those. Flagged is inbox-only and lives here rather than
 * as a fourth segment, which the design sizes for equal thirds.
 */
const INBOX_CHIP_IDS = [
  FILTER_IDS.FLAGGED,
  FILTER_IDS.DIET_STALE,
  FILTER_IDS.WORKOUT_STALE,
  FILTER_IDS.NO_PLAN,
  FILTER_IDS.TRACKING,
  FILTER_IDS.NOT_TRACKING,
  FILTER_IDS.NEW,
];

/** Everything that needs a count, including the two the segments own. */
const INBOX_COUNT_IDS = [FILTER_IDS.UNREAD, FILTER_IDS.TAGGED, ...INBOX_CHIP_IDS];

/**
 * Client display name — resolves the real client name from the cached
 * identity lookup, falling back to a display-id label when it can't be
 * resolved (server omits `client_name` and the client isn't in the cache yet).
 * @param {Object} room
 * @param {(clientId: string|number) => string|undefined} [getClientName]
 * @returns {string}
 */
const roomDisplayName = (room, getClientName) =>
  room.client_name || getClientName?.(room.client_id) || `Client #${room.display_id}`;

/**
 * Initials from a display name (first letters of first two words).
 * @param {string} name
 * @returns {string}
 */
const getInitials = (name) => {
  if (!name) return "";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
};

/**
 * A room needs attention if it is flagged / marked for AI review.
 * The backend field name isn't finalised, so we accept any of the plausible
 * markers.
 * @param {Object} room
 * @returns {boolean}
 */
const isFlagged = (room) =>
  Boolean(room.flagged || room.needs_attention || room.ai_review_pending);

/** @param {Object} room */
const isAiReview = (room) => Boolean(room.ai_review_pending);

/** @param {Object} room */
const isUnread = (room) => Number(room.unread_count) > 0;

/** @param {Object} room */
const isPinned = (room) =>
  Boolean(room.sla_active) || isFlagged(room) || isAiReview(room);

/**
 * Projects a raw room onto the shape the shared filters expect. The only place
 * this panel's row type and the dock's are reconciled.
 *
 * @param {Object} room
 * @param {Set<string>} mentionRoomIds
 * @returns {{clientId: string, unread: number, mentioned: boolean, flagged: boolean}}
 */
const subjectFromRoom = (room, mentionRoomIds) => ({
  clientId: room.client_id == null ? "" : String(room.client_id),
  unread: Number(room.unread_count) || 0,
  mentioned: isUnread(room) && mentionRoomIds.has(String(room.id)),
  flagged: isFlagged(room),
});

/**
 * Sort comparator: pinned rooms first, then by last activity
 * (`last_activity_at` desc, falling back to `created_at` for a room with no
 * messages yet). Never `last_seq` — it is a per-room counter, not a time.
 * @param {Object} a
 * @param {Object} b
 * @returns {number}
 */
const byActivity = (a, b) => {
  const pinDelta = Number(isPinned(b)) - Number(isPinned(a));
  if (pinDelta !== 0) return pinDelta;

  const time = (room) => {
    const at = room.last_activity_at || room.created_at;
    return at ? Date.parse(at) || 0 : 0;
  };
  return time(b) - time(a);
};

/**
 * Neutral client avatar. Clients have no coach color, so we render a plain
 * gray-ringed circle — deliberately distinct from the colored coach rings that
 * `AvatarRing` produces.
 *
 * @param {Object} props
 * @param {string} props.name
 * @param {boolean} [props.showDot] - Show the unread presence dot.
 */
const ClientAvatar = ({ name, showDot = false }) => {
  const initials = getInitials(name);
  return (
    <span className="relative inline-flex flex-shrink-0">
      <span
        role="img"
        aria-label={name || "Client"}
        className="inline-flex items-center justify-center w-9 h-9 rounded-full ring-2 ring-gray-200 bg-gray-100 text-gray-600 text-sm font-semibold"
      >
        {initials || "?"}
      </span>
      {showDot && (
        <span
          aria-label="Unread messages"
          className="absolute top-0 right-0 block w-2.5 h-2.5 rounded-full ring-2 ring-white bg-orange-primary"
        />
      )}
    </span>
  );
};

/**
 * Single conversation row (full mode). One status affordance is shown on the
 * right, chosen by priority: paused -> AI-review -> needs-attention -> unread.
 *
 * @param {Object} props
 * @param {Object} props.room
 * @param {boolean} props.selected
 * @param {(room: Object) => void} props.onSelect
 * @param {(clientId: string|number) => string|undefined} [props.getClientName]
 * @param {boolean} [props.mentioned] - Room holds an unread @-mention of the viewer.
 * @param {Object} [props.tags] - Coaching signals from `deriveTags`.
 * @param {string|null} [props.timeLabel] - Relative last-activity time.
 */
const InboxRow = ({
  room,
  selected,
  onSelect,
  getClientName,
  mentioned = false,
  tags,
  timeLabel = null,
}) => {
  const name = roomDisplayName(room, getClientName);
  const paused = room.status === "paused";

  // Priority order for the single status affordance.
  let affordance = null;
  if (paused) {
    affordance = <StatusBadge tone="neutral">Paused · read-only</StatusBadge>;
  } else if (isAiReview(room)) {
    affordance = (
      <StatusBadge tone="ai" dot>
        AI replied · review
      </StatusBadge>
    );
  } else if (isFlagged(room)) {
    affordance = (
      <span
        aria-label="Needs attention"
        className="block w-2.5 h-2.5 rounded-full bg-amber-500"
      />
    );
  } else if (isUnread(room)) {
    affordance = (
      <StatusBadge tone="brand">
        {mentioned ? `@ ${room.unread_count}` : room.unread_count}
      </StatusBadge>
    );
  } else {
    // Lowest rung: a read, unflagged row is exactly where a coaching signal has
    // room to speak. It never competes with the unread count above it.
    affordance = <RosterSignalChip tags={tags} />;
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(room)}
      aria-current={selected ? "true" : undefined}
      className={`relative flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors sm:py-2.5 ${
        selected
          ? "bg-light-orange"
          : mentioned
          ? "bg-light-orange/70 ring-1 ring-orange-primary/40 hover:bg-light-orange"
          : "hover:bg-orange-50"
      } ${paused ? "opacity-60" : ""}`}
    >
      {selected && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-full bg-orange-primary"
        />
      )}
      <ClientAvatar name={name} showDot={isUnread(room)} />
      <span className="flex-1 min-w-0">
        <span className="flex items-baseline gap-2">
          <span
            className={`min-w-0 flex-1 truncate text-sm ${
              isUnread(room) ? "font-semibold text-gray-900" : "font-medium text-gray-800"
            }`}
          >
            {name}
          </span>
          {timeLabel && (
            <span
              className={`flex-shrink-0 text-[11px] tabular-nums ${
                isUnread(room) ? "font-semibold text-bc-accent-text" : "text-gray-400"
              }`}
            >
              {timeLabel}
            </span>
          )}
        </span>
        {/* Typing hook-in point: per-room typing would render <TypingIndicator />
            here once this row subscribes to the room channel. */}
        <span
          className={`block truncate text-xs ${
            mentioned ? "font-medium text-orange-primary" : "text-gray-500"
          }`}
        >
          {room.last_message || "No messages yet"}
        </span>
      </span>
      {affordance && <span className="flex-shrink-0">{affordance}</span>}
    </button>
  );
};

/**
 * Collapsed avatar-only strip row (client-focus mode).
 *
 * @param {Object} props
 * @param {Object} props.room
 * @param {boolean} props.selected
 * @param {(room: Object) => void} props.onSelect
 * @param {(clientId: string|number) => string|undefined} [props.getClientName]
 */
const StripRow = ({ room, selected, onSelect, getClientName }) => {
  const name = roomDisplayName(room, getClientName);
  return (
    <button
      type="button"
      onClick={() => onSelect(room)}
      aria-current={selected ? "true" : undefined}
      title={name}
      className={`flex items-center justify-center py-2 rounded-lg transition-colors ${
        selected ? "bg-light-orange" : "hover:bg-orange-50"
      } ${room.status === "paused" ? "opacity-60" : ""}`}
    >
      <ClientAvatar name={name} showDot={isUnread(room)} />
    </button>
  );
};

/**
 * Coaching inbox list. Loads COACHING rooms, supports debounced search,
 * client-side All/Unread/Flagged filters, live refresh via the user channel
 * plus a polling fallback, and two render modes.
 *
 * @param {Object} props
 * @param {string} [props.selectedRoomId] - Currently selected room id.
 * @param {(room: Object) => void} props.onSelectRoom - Receives the full room.
 * @param {string|number} [props.viewerUserId] - Viewer id for the user channel.
 * @param {'full'|'strip'} [props.mode='full'] - Render mode.
 */
export function InboxList({
  selectedRoomId,
  onSelectRoom,
  viewerUserId,
  mode = "full",
}) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // Multi-select and AND'ed. An empty set is "All".
  const [activeFilters, setActiveFilters] = useState(() => new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersAnchorRef = useRef(null);
  const { getClientName } = useCommsIdentity();
  // Rooms holding an unread @-mention of the viewer (session-local set).
  const { mentionRoomIds } = useRoomsUnread();
  const {
    getTags,
    hasInsights,
    thresholdDays,
    setThresholdDays,
    insightsVersion,
  } = useClientInsights();

  // Debounce the search input (~300ms) before it drives a refetch.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const fetchRooms = useCallback(
    async ({ withSpinner = false } = {}) => {
      if (withSpinner) setLoading(true);
      try {
        const params = { room_type: "COACHING", limit: ROOMS_PAGE_LIMIT };
        if (search) params.search = search;
        const data = await listRooms(commsGet, params);
        setRooms(Array.isArray(data?.rooms) ? data.rooms : []);
        setError(null);
      } catch (err) {
        setError(err?.message || "Error fetching conversations");
      } finally {
        if (withSpinner) setLoading(false);
      }
    },
    [search]
  );

  // Initial load + refetch whenever the debounced search term changes.
  useEffect(() => {
    fetchRooms({ withSpinner: true });
  }, [fetchRooms]);

  // Debounced background refresh, shared by realtime events and polling.
  const refreshTimer = useRef(null);
  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => {
      fetchRooms();
    }, REFRESH_DEBOUNCE_MS);
  }, [fetchRooms]);

  // Live refresh: any notification on the user channel triggers a debounced
  // refetch. Degrades gracefully when Centrifugo is unavailable.
  useUserChannel(viewerUserId, { onNotification: scheduleRefresh });

  // Polling fallback every 30s.
  useEffect(() => {
    const interval = setInterval(() => fetchRooms(), POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [fetchRooms]);

  // Mentions are session-local, like the badge: only ones seen since app load
  // (see RoomsUnreadProvider).
  const subjects = useMemo(
    () => rooms.map((room) => subjectFromRoom(room, mentionRoomIds)),
    [rooms, mentionRoomIds]
  );

  // `getTags` is stable by design, so `insightsVersion` is what tells these
  // memos a batch landed. Without it the chips would sit at zero forever.
  const counts = useMemo(
    () => countMatches(subjects, getTags, INBOX_COUNT_IDS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subjects, getTags, insightsVersion]
  );

  const chipIds = useMemo(
    () => availableFilterIds(subjects, getTags, INBOX_CHIP_IDS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subjects, getTags, insightsVersion]
  );

  const visibleRooms = useMemo(() => {
    const filtered = rooms.filter((room, index) =>
      matchesFilters(subjects[index], getTags(subjects[index].clientId), activeFilters)
    );
    const tiebreak = byActivity;
    if (!hasInsightFilter(activeFilters)) return [...filtered].sort(tiebreak);

    // The shared comparator reads `clientId`, so adapt it to raw rooms.
    const byStaleness = compareByStalenessDesc(getTags, tiebreak);
    return [...filtered].sort((a, b) =>
      byStaleness(
        { clientId: String(a.client_id), ...a },
        { clientId: String(b.client_id), ...b }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, subjects, activeFilters, getTags, insightsVersion]);

  const emptyState = describeEmptyState(activeFilters, search);

  const handleSelect = useCallback(
    (room) => {
      onSelectRoom?.(room);
    },
    [onSelectRoom]
  );

  // ---- Strip mode: collapsed avatar-only column. ----
  if (mode === "strip") {
    return (
      <div className="w-14 flex flex-col gap-1 px-1 py-2 overflow-y-auto">
        {loading ? (
          <Loader />
        ) : (
          visibleRooms.map((room) => (
            <StripRow
              key={room.id}
              room={room}
              selected={room.id === selectedRoomId}
              onSelect={handleSelect}
              getClientName={getClientName}
            />
          ))
        )}
      </div>
    );
  }

  // ---- Full mode. ----
  return (
    <div className="flex flex-col h-full min-h-0 bg-white">
      <div className="px-3 pt-3 pb-2 border-b border-gray-100">
        <div className="relative flex items-center px-3 py-2 bg-white border border-gray-200 rounded-xl shadow-sm focus-within:border-orange-primary transition-colors">
          <BsSearch size={16} className="text-gray-400 mr-2 flex-shrink-0" />
          <input
            type="text"
            aria-label="Search conversations"
            placeholder="Search conversations..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-base text-gray-900 outline-none placeholder-gray-400 sm:text-sm"
          />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <SegmentedControl
            className="flex-1"
            label="Filter conversations"
            value={currentSegment(activeFilters)}
            onChange={(id) => setActiveFilters((prev) => setSegment(prev, id))}
            items={SEGMENTS.map((segment) => ({
              id: segment.id,
              label: segment.label,
              count: segment.filterId ? counts[segment.filterId] : counts.all,
            }))}
          />
          {hasInsights && (
            <FiltersButton
              ref={filtersAnchorRef}
              expanded={filtersOpen}
              activeCount={countActiveInsightFilters(activeFilters)}
              onClick={() => setFiltersOpen((open) => !open)}
            />
          )}
        </div>
      </div>

      {hasInsights && (
        <FiltersPopover
          open={filtersOpen}
          anchorRef={filtersAnchorRef}
          onClose={() => setFiltersOpen(false)}
          panelProps={{
            activeIds: activeFilters,
            onToggle: (id) => setActiveFilters((prev) => toggleFilter(prev, id)),
            counts,
            chipIds,
            thresholdDays,
            onThresholdChange: setThresholdDays,
          }}
        />
      )}

      <div className="flex-1 min-h-0 overflow-y-auto px-2 py-2">
        {loading ? (
          <Loader />
        ) : error ? (
          <p className="px-2 py-6 text-sm text-danger-dark text-center">{error}</p>
        ) : visibleRooms.length === 0 ? (
          <div className="px-2 py-6 text-center">
            <p className="text-sm font-semibold text-gray-700">{emptyState.title}</p>
            <p className="mt-1 text-xs text-gray-400">{emptyState.body}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {visibleRooms.map((room) => (
              <InboxRow
                key={room.id}
                room={room}
                selected={room.id === selectedRoomId}
                onSelect={handleSelect}
                getClientName={getClientName}
                mentioned={mentionRoomIds.has(String(room.id))}
                tags={getTags(room.client_id)}
                timeLabel={
                  room.last_activity_at ? relativeTime(room.last_activity_at) : null
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default InboxList;
